import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface GoogleLoginButtonProps {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: GoogleInitConfig) => void;
                    renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
                    prompt: () => void;
                };
            };
        };
    }
}

interface GoogleInitConfig {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
}

interface GoogleButtonConfig {
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    logo_alignment?: 'left' | 'center';
    width?: number;
}

interface GoogleCredentialResponse {
    credential: string;
    select_by?: string;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ onSuccess, onError }) => {
    const { login } = useAuth();
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const buttonRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Load Google Identity Services script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => setIsGoogleLoaded(true);
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        if (!isGoogleLoaded || !window.google || !buttonRef.current) return;

        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId) {
            console.error('VITE_GOOGLE_CLIENT_ID is not set');
            return;
        }

        // Initialize Google Sign-In
        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleResponse,
        });

        // Render the button
        window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'pill',
            width: 280,
        });
    }, [isGoogleLoaded]);

    const handleGoogleResponse = async (response: GoogleCredentialResponse) => {
        try {
            // Send Google credential to backend
            const { user, token } = await fetch(
                `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/auth/google`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: response.credential }),
                }
            ).then((res) => res.json()).then((data) => data.data);

            // Save token and update auth state
            await login(token);

            console.log('Login successful:', user);
            onSuccess?.();
        } catch (error) {
            console.error('Login failed:', error);
            onError?.(error as Error);
        }
    };

    if (!isGoogleLoaded) {
        return (
            <div className="flex items-center justify-center p-3 border border-gray-300 rounded-full w-[280px]">
                <span className="text-gray-500 text-sm">載入中...</span>
            </div>
        );
    }

    return <div ref={buttonRef}></div>;
};

export default GoogleLoginButton;
